/**
 * Typed, validated access to build-time configuration.
 *
 * Everything here is injected at build time via Vite `VITE_*` env vars (see
 * `.env.example`). All values are PUBLIC — the Firebase web config is not a
 * secret, and the backend is the only security boundary (it re-enforces the
 * admin claim on every write; Firestore rules gate every read). Never put a
 * server secret in this file or any `VITE_*` var.
 */

export type EliosEnv = "dev" | "staging" | "production";

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(
      `Missing required build-time env var ${name}. ` +
        `Copy .env.example to .env.<mode> and fill it in (see README).`,
    );
  }
  return value;
}

const rawEnv = import.meta.env.VITE_ELIOS_ENV;
const eliosEnv: EliosEnv =
  rawEnv === "staging" || rawEnv === "production" ? rawEnv : "dev";

export const env = {
  /** Logical environment for the env banner + prod-gating of destructive actions. */
  eliosEnv,
  isProd: eliosEnv === "production",
  isStaging: eliosEnv === "staging",
  isDev: eliosEnv === "dev",

  /** Backend admin API base URL (no trailing slash). */
  apiBaseUrl: required(import.meta.env.VITE_API_BASE_URL, "VITE_API_BASE_URL").replace(/\/+$/, ""),

  /** Google Workspace hosted-domain SSO is restricted to. Empty string = no restriction (dev only). */
  allowedHostedDomain: import.meta.env.VITE_ALLOWED_HD ?? "",

  firebase: {
    apiKey: required(import.meta.env.VITE_FIREBASE_API_KEY, "VITE_FIREBASE_API_KEY"),
    authDomain: required(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, "VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: required(import.meta.env.VITE_FIREBASE_PROJECT_ID, "VITE_FIREBASE_PROJECT_ID"),
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
    appId: required(import.meta.env.VITE_FIREBASE_APP_ID, "VITE_FIREBASE_APP_ID"),
  },
} as const;

/** Human label for the current environment (used in the banner + document title). */
export const ENV_LABEL: Record<EliosEnv, string> = {
  dev: "DEV",
  staging: "STAGING",
  production: "PRODUCTION",
};
